import { useCallback, useEffect, useMemo } from 'react';
import { useMessagesContext } from '@/contexts/messages';
import { StatusEntry, UiMessage } from '@/types/message';

export type UseMessagesResult = {
  messages: UiMessage[];
  statusTrail: StatusEntry[];
  sendStreaming: (
    content: string,
    sheetId: number | null,
    options?: {
      onChatResolved?: (chatId: number, meta?: { title?: string }) => void;
      categoryIds?: number[];
      workflow?: string;
      files?: File[];
    }
  ) => Promise<number | null>;
  regenerate: (
    content: string,
    sheetId: number | null,
    messageId: number | string,
    mssageTitle: string,
    options?: {
      onChatResolved?: (chatId: number, meta?: { title?: string }) => void;
      categoryIds?: number[];
      workflow?: string;
    }
  ) => Promise<number | null>;
  cancelStreaming: () => void;
};

const keyOf = (accountId: string | number, chatId: string | number) => `${accountId}:${chatId}`;
const pendingKeyOf = (accountId: string | number) => `pending:${accountId}`;

export const useMessages = (
  accountId?: string | number | null,
  chatId?: string | number | null,
  options?: { skipHistoryLoad?: boolean }
): UseMessagesResult => {
  const skipHistoryLoad = options?.skipHistoryLoad ?? false;
  const { state, load, startRegenerate, sendStreaming, cancelStreaming } = useMessagesContext();

  const key = useMemo(() => {
    if (accountId == null) return null;
    if (chatId == null) return pendingKeyOf(accountId);
    return keyOf(accountId, chatId);
  }, [accountId, chatId]);

  const bucket = key ? state[key] : undefined;

  useEffect(() => {
    if (accountId == null || chatId == null) return;
    if (skipHistoryLoad) return;
    void load(accountId, chatId);
  }, [accountId, chatId, load, skipHistoryLoad]);

  const messages = bucket?.data ?? [];
  const statusTrail = bucket?.statusTrail ?? [];

  const sendStreamingMessage = useCallback<UseMessagesResult['sendStreaming']>(
    async (content, sheetId, options) => {
      if (accountId == null) return null;
      return sendStreaming(
        accountId,
        chatId ?? null,
        {
          content,
          sheetId: sheetId ?? undefined,
          categoryIds: options?.categoryIds,
          workflow: options?.workflow,
          files: options?.files,
        },
        options
      );
    },
    [accountId, chatId, sendStreaming]
  );

  const regenerate = useCallback<UseMessagesResult['regenerate']>(
    async (content, sheetId, messageId, mssageTitle, options) => {
      if (accountId == null || chatId == null) return null;
      await startRegenerate(accountId, chatId, messageId, mssageTitle);
      return sendStreaming(
        accountId,
        chatId ?? null,
        {
          content,
          sheetId: sheetId ?? undefined,
          messageId,
          categoryIds: options?.categoryIds,
          workflow: options?.workflow,
        },
        options
      );
    },
    [accountId, chatId, startRegenerate, sendStreaming]
  );

  const onCancelStreaming = useCallback<UseMessagesResult['cancelStreaming']>(() => {
    cancelStreaming();
  }, [cancelStreaming]);

  return {
    messages,
    statusTrail,
    regenerate,
    sendStreaming: sendStreamingMessage,
    cancelStreaming: onCancelStreaming,
  };
};

export type { UiMessage };

export default useMessages;
